import SlideFrame from "./_SlideFrame";
import { Smartphone, Bell, RefreshCw, TrendingUp, Lock, CheckCircle2 } from "lucide-react";

const lenderValue = [
  {
    icon: Bell,
    t: "Push notifications drive faster resolutions",
    d: "Instant in-app reminders for upcoming payments, NSF events, and missed-payment recovery — measurably reduces days-past-due before a human has to touch the file.",
  },
  {
    icon: RefreshCw,
    t: "Self-serve everything",
    d: "Borrowers see balance, next due date, payoff quote, and full payment history; toggle autopay on or off; update payment methods — without calling your team.",
  },
  {
    icon: TrendingUp,
    t: "Re-marketing channel for the lender",
    d: "Surface targeted offers to borrowers in good standing — e.g. a new credit line for borrowers 80%+ paid down. The repayment journey becomes the next origination funnel.",
  },
  {
    icon: Lock,
    t: "Brandable, white-label-ready",
    d: "Operates behind your brand. The borrower sees your logo, your tone — IBA stays in the back office.",
  },
];

const screenSpec = [
  { k: "Next due", v: "$19.34 · Dec 17, 2025" },
  { k: "Remaining payments", v: "8" },
  { k: "Current payoff", v: "$133.07" },
  { k: "Principal remaining", v: "$131.34" },
  { k: "Percent paid off", v: "18.17%" },
  { k: "Autopay", v: "ON", highlight: true },
];

function PhoneFrame() {
  return (
    <div className="relative mx-auto" style={{ width: 280 }}>
      <div className="rounded-[32px] border-[10px] border-foreground bg-background shadow-[0_30px_60px_-20px_rgba(0,0,0,0.25)] overflow-hidden">
        {/* Notch */}
        <div className="h-6 bg-foreground flex items-center justify-center">
          <span className="w-16 h-1.5 bg-background/30 rounded-full" />
        </div>
        {/* App content */}
        <div className="bg-muted/40 p-4 min-h-[480px]">
          <div className="flex items-center justify-between mb-4">
            <span className="overline text-primary">IBA-powered portal</span>
            <span className="text-[10px] font-mono-data text-muted-foreground">9:41</span>
          </div>
          <p className="font-display text-base leading-snug">
            Hi <span className="text-primary">Alex</span>,<br />
            you&rsquo;re <span className="font-semibold">18.17%</span> paid off.
          </p>
          {/* Donut */}
          <div className="my-5 flex items-center justify-center">
            <svg width="140" height="140" viewBox="0 0 100 100" className="-rotate-90">
              <circle cx="50" cy="50" r="40" stroke="currentColor" strokeOpacity="0.12" strokeWidth="12" fill="none" />
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="currentColor"
                className="text-primary"
                strokeWidth="12"
                fill="none"
                strokeDasharray={`${0.1817 * 251.327} 251.327`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute text-center pt-1">
              <p className="font-mono-data text-[10px] text-muted-foreground">Payoff</p>
              <p className="font-display text-2xl">$133.07</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-y-2 text-[11px] font-mono-data mb-4">
            {screenSpec.map((s) => (
              <span key={s.k} className="contents">
                <span className="text-muted-foreground">{s.k}</span>
                <span className={`text-right tabular ${s.highlight ? "text-primary font-semibold" : ""}`}>
                  {s.v}
                </span>
              </span>
            ))}
          </div>
          <button className="w-full bg-foreground text-background py-2.5 text-xs font-mono-data uppercase tracking-brand">
            Make payment
          </button>
        </div>
      </div>
      {/* Notification bubble */}
      <div className="absolute -right-4 top-24 hidden lg:block">
        <div className="bg-background border border-foreground p-3 max-w-[200px] shadow-md">
          <div className="flex items-center gap-2 mb-1">
            <Bell className="w-3 h-3 text-primary" strokeWidth={2} />
            <span className="overline text-primary">Push</span>
          </div>
          <p className="text-[11px] leading-snug font-display">
            Your payment of $19.34 posts in 3 days. Tap to pay now.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SlideBorrowerApp() {
  return (
    <SlideFrame tag="03 · The Product">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div className="max-w-3xl">
          <p className="overline mb-4 flex items-center gap-3">
            <Smartphone className="w-4 h-4 text-primary" strokeWidth={1.5} />
            Direct-to-consumer · web + mobile
          </p>
          <h2 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95] font-medium">
            A borrower app that works <em className="italic text-primary">for the lender</em>, too.
          </h2>
        </div>
        <p className="text-sm text-muted-foreground max-w-sm">
          Modern lending isn&rsquo;t complete without a modern borrower experience. Ours doubles as
          a retention and re-marketing channel.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5 flex justify-center pt-2">
          <PhoneFrame />
        </div>
        <div className="lg:col-span-7">
          <p className="overline mb-4">Why it matters to you</p>
          <div className="grid sm:grid-cols-2 gap-px bg-foreground/20 border border-foreground/20">
            {lenderValue.map(({ icon: Icon, t, d }, i) => (
              <article
                key={i}
                className="bg-background p-6 min-h-[170px]"
                data-testid={`borrower-app-value-${i + 1}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  <span className="font-mono-data text-[11px] uppercase tracking-brand text-muted-foreground">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-display text-xl leading-tight mb-2">{t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
              </article>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] uppercase tracking-brand text-muted-foreground font-mono-data">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary" /> iOS</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary" /> Android</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary" /> Web portal</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary" /> Autopay & one-time pay</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary" /> Statement & payoff</span>
          </div>
        </div>
      </div>

      <p className="mt-8 text-sm text-muted-foreground italic font-display text-xl max-w-4xl">
        Screenshots shown are an example of the live IBA-powered borrower portal — every label,
        amount, and date is illustrative.
      </p>
    </SlideFrame>
  );
}
